import React from 'react';
import { Link } from 'react-router-dom';
import SeatRows from '../booking/shared/SeatRows';
import PaymentChoice from '../booking/shared/PaymentChoice';
import type { GuestSeat } from '../../models/booking/GuestSeat';
import type { ClaimRaceNightBody, RaceNight, RaceNightGuestSeat, RigClass } from '../../models/raceNights/RaceNight';
import type { PaymentOptionDto } from '../../models/sessions/Session';
import {
  atIncludedLimit, canSeatUseNight, claimQuote, effectivePayment, includedLimitLine, money, optionReason, ordinal,
  rigsFit, rigsOffered,
} from '../../utils/raceNightFormat';

export type MemberPayment = NonNullable<ClaimRaceNightBody['payment']>;
export type GuestPayment = NonNullable<RaceNightGuestSeat['payment']>;

export interface RaceNightPanelProps {
  night: RaceNight;
  memberName: string;
  /** The member's tier, for the card price's label; null for a non-member. */
  tierName: string | null;
  balance: number | null;
  rig: RigClass;
  onRigChange: (r: RigClass) => void;
  /** The member's choice for their own seat; null until they pick (the first available is used). */
  payment: MemberPayment | null;
  onPaymentChange: (p: MemberPayment) => void;
  guests: RaceNightGuestSeat[];
  onGuestsChange: (g: RaceNightGuestSeat[]) => void;
  busy: boolean;
  error: { message: string; buyCredits?: boolean } | null;
  onClaim: () => void;
  onJoinWaitlist: () => void;
  onLeaveWaitlist: () => void;
}

/** "{name}, 20% off" when the price is below the full ticket, else "Full price". */
const offLabel = (cents: number, fullCents: number, name: string): string => {
  const pct = fullCents > 0 ? Math.round((1 - cents / fullCents) * 100) : 0;
  return pct > 0 ? `${name}, ${pct}% off` : 'Full price';
};

const creditsText = (n: number) => `${n} ${n === 1 ? 'credit' : 'credits'}`;

const NO_CARD_ON_RIG = 'Card isn’t offered on this rig.';

/**
 * The member's own options as PaymentChoice shows them. The wire prices card at the PRO ticket, so the
 * card row is re-priced at the rig chosen (cardPriceCents), and reason codes become words.
 */
const memberChoices = (night: RaceNight, rig: RigClass, balance: number | null, tierName: string | null): PaymentOptionDto[] =>
  night.options.map((o) => {
    const reason = optionReason(o.reason, night.includedLimit);
    if (o.payment === 'card') {
      const cents = night.cardPriceCents?.[rig];
      if (cents === undefined) return { ...o, available: false, reason: NO_CARD_ON_RIG };
      const name = tierName ? `${tierName} price` : 'Member price';
      return { ...o, priceCents: cents, reason, label: offLabel(cents, night.ticketPriceCents[rig], name) };
    }
    if (o.payment === 'credits') {
      return { ...o, reason, label: balance !== null ? `${creditsText(o.credits)} · you have ${balance}` : undefined };
    }
    const limit = night.includedLimit;
    const held = limit && typeof limit.max === 'number' ? `You're holding ${limit.held} of ${limit.max} included nights` : undefined;
    return { ...o, reason, label: held };
  });

/** One guest's options: card at the guest rate for THEIR rig, or the member's credits. */
const guestChoices = (night: RaceNight, rig: RigClass): PaymentOptionDto[] =>
  (night.guestOptions ?? []).map((o) => {
    const reason = optionReason(o.reason, night.includedLimit);
    if (o.payment === 'card') {
      const cents = night.guestCardPriceCents?.[rig];
      if (cents === undefined) return { ...o, available: false, reason: NO_CARD_ON_RIG };
      return { ...o, priceCents: cents, reason, label: offLabel(cents, night.ticketPriceCents[rig], 'Guest rate') };
    }
    return { ...o, reason, label: 'From your credits' };
  });

const RIG_NAME: Record<RigClass, string> = { pro: 'Pro', esports: 'Esports' };

/**
 * A seat needs a payment choice only when there is one to make. A single usable option (card, most often)
 * is already priced on the seat row and in the total, so the box only repeated it. A single option that
 * can't be used is still shown: it says why the seat can't be claimed.
 */
const needsChoice = (options: PaymentOptionDto[]): boolean =>
  options.length > 1 || options.some((o) => !o.available);

/** What opens under a night: the claim, the waitlist, or the member's own seat. */
const RaceNightPanel: React.FC<RaceNightPanelProps> = ({
  night, memberName, tierName, balance, rig, onRigChange, payment, onPaymentChange, guests, onGuestsChange,
  busy, error, onClaim, onJoinWaitlist, onLeaveWaitlist,
}) => {
  const errorBox = error && (
    <div className="alert alert-warning mb-0" role="alert">
      {error.message}
      {error.buyCredits && <> <Link to="/checkout?returnTo=race-nights" className="alert-link">Buy credits</Link></>}
    </div>
  );

  if (night.mine === 'seated') {
    return (
      <div className="rn-panel" data-testid="night-panel">
        <p className="mb-0">
          You&apos;re in on a {night.myRig === 'esports' ? 'Esports' : 'Pro'} rig.
          {night.confirmationCode && <> Confirmation code <strong>{night.confirmationCode}</strong>.</>}
        </p>
        {/* Cancelling is staff-managed (spec Decision 5): no button here, by design. */}
        <p className="rn-hint mb-0">To cancel, contact the venue.</p>
      </div>
    );
  }

  if (night.mine === 'paymentPending') {
    // The card step lives with the page that started it (it holds the payment handle). Reaching this
    // panel means another tab or device: say what the seats are, never offer a second claim.
    return (
      <div className="rn-panel" data-testid="night-panel">
        <p className="mb-0">
          Your seats are held while the card is taken. They aren&apos;t booked yet.
        </p>
        <p className="rn-hint mb-0">
          Finish paying where you started. An unpaid hold is let go after 15 minutes, and any credits it took come back.
        </p>
      </div>
    );
  }

  if (night.mine === 'waitlisted') {
    return (
      <div className="rn-panel" data-testid="night-panel">
        <p className="mb-0">
          You&apos;re <strong>{ordinal(night.waitlistPosition ?? 1)}</strong> on the waitlist. If a seat opens, the
          venue will contact you to confirm it. Nothing is charged until then.
        </p>
        <div className="wizard-summary">
          <button type="button" className="btn btn-outline-light ms-auto" onClick={onLeaveWaitlist} disabled={busy}>
            {busy ? 'Leaving…' : 'Leave waitlist'}
          </button>
        </div>
        {errorBox}
      </div>
    );
  }

  if (night.seats.open === 0) {
    return (
      <div className="rn-panel" data-testid="night-panel">
        <p className="mb-0">
          This night is full. Join the waitlist and the venue will contact you if a seat opens.
          It&apos;s for you only; guests can&apos;t be waitlisted.
        </p>
        <div className="wizard-summary">
          <span className="wizard-summary-line">Queue: {night.seats.waiting} waiting</span>
          <button type="button" className="btn btn-primary ms-auto" onClick={onJoinWaitlist} disabled={busy}>
            {busy ? 'Joining…' : 'Join waitlist'}
          </button>
        </div>
        {errorBox}
      </div>
    );
  }

  // Guests only on a Public night (Q1); the server lists guest options only where they are allowed.
  const guestsAllowed = night.access === 'public' && Boolean(night.guestOptions?.length) && Boolean(night.guestCardPriceCents);
  const offered = rigsOffered(night);
  const rigs: RigClass[] = [rig, ...guests.map((g) => g.rigClass)];
  const fits = rigsFit(night, rigs);
  const named = guests.every((g) => g.firstName.trim() && (g.lastName ?? '').trim());

  const memberPay = effectivePayment<MemberPayment>(night.options, payment);
  const guestPays = guests.map((g) => effectivePayment<GuestPayment>(night.guestOptions, g.payment ?? 'card'));
  const payable = memberPay !== null && guestPays.every((p) => p !== null);
  // A QUOTE only, before tax: the charge is the server's amountMinor on the 202.
  const quote = claimQuote(night, rig, memberPay, guests.map((g, i) => ({ rigClass: g.rigClass, payment: guestPays[i] })));
  const short = quote.credits > 0 && balance !== null && balance < quote.credits;

  const memberOptions = memberChoices(night, rig, balance, tierName);
  const memberOption = memberOptions.find((o) => o.payment === memberPay);

  const parts: string[] = [];
  if (memberPay === 'included') parts.push('Your seat included');
  if (memberPay === 'credits') parts.push(`Your seat ${creditsText(memberOption?.credits ?? 0)}`);
  if (memberPay === 'card') parts.push('Your seat by card');
  const byCard = guestPays.filter((p) => p === 'card').length;
  const byCredits = guestPays.filter((p) => p === 'credits').length;
  if (byCard) parts.push(`${byCard} guest${byCard === 1 ? '' : 's'} by card`);
  if (byCredits) parts.push(`${byCredits} guest${byCredits === 1 ? '' : 's'} with credits`);
  if (quote.credits > 0 && balance !== null) {
    parts.push(short
      ? `You have ${balance} credits. These seats need ${quote.credits}.`
      : `Balance ${balance} credits · ${balance - quote.credits} left after this`);
  }
  const line = guests.length === 0 && memberPay === 'included' ? 'Your seat is included' : parts.join(' · ');
  const amount = [quote.credits > 0 ? creditsText(quote.credits) : null, quote.cardCents > 0 ? money(quote.cardCents) : null]
    .filter(Boolean).join(' + ');

  const buttonLabel = quote.cardCents > 0
    ? 'Continue to card'
    : quote.credits > 0 ? `Claim for ${quote.credits} credits` : 'Claim seat';
  // A new guest sits beside the member when that class has room, otherwise on the other class.
  const nextGuestRig: RigClass = canSeatUseNight(night, [...rigs, rig], rigs.length, rig) ? rig : (rig === 'pro' ? 'esports' : 'pro');
  const canAddGuest = offered.includes(nextGuestRig) && rigsFit(night, [...rigs, nextGuestRig]);

  let seatLabel = '';
  if (memberPay === 'included') seatLabel = 'Included';
  if (memberPay === 'credits') seatLabel = creditsText(memberOption?.credits ?? 0);
  if (memberPay === 'card') seatLabel = money(memberOption?.priceCents ?? 0);
  const seatTone = memberPay === 'credits' ? 'credits' : memberPay === 'card' ? 'card' : 'included';
  // A guest whose only way to pay is card gets no choice box, so their price goes on their own row.
  const cardOnlyGuestCents = (r: RigClass): number | undefined => {
    const options = guestChoices(night, r);
    return !needsChoice(options) && options[0]?.payment === 'card' ? options[0].priceCents : undefined;
  };

  return (
    <div className="rn-panel" data-testid="night-panel">
      <div>
        <span className="wizard-label">{guestsAllowed ? 'Who is racing' : 'Your seat'}</span>
        <SeatRows
          seats={guestsAllowed ? guests : []}
          memberName={memberName}
          memberRigClass={rig}
          onMemberRigChange={onRigChange}
          canUse={(seatIndex, r) => canSeatUseNight(night, rigs, seatIndex, r)}
          // SeatRows edits the seats it is given by spreading them, so each guest keeps its payment.
          onChange={(next: GuestSeat[]) => onGuestsChange(next as RaceNightGuestSeat[])}
          memberPriceLabel={seatLabel}
          memberPriceTone={seatTone}
          proSeatCents={cardOnlyGuestCents('pro')}
          esportsSeatCents={cardOnlyGuestCents('esports')}
          rigs={offered}
        />
      </div>

      {guestsAllowed ? (
        <button type="button" className="rn-add-guest" disabled={!canAddGuest}
          onClick={() => onGuestsChange([...guests, { firstName: '', rigClass: nextGuestRig, experienceLevel: 'beginner', payment: 'card' }])}>
          + Add a guest
        </button>
      ) : (
        <p className="rn-note-box mb-0">This is a members-only night, so it&apos;s just your seat. Guests can join public nights.</p>
      )}

      {(atIncludedLimit(night) || needsChoice(memberOptions)
        || (guestsAllowed && guests.some((g) => needsChoice(guestChoices(night, g.rigClass))))) && (
      <div className="rn-pay">
        {atIncludedLimit(night) && night.includedLimit && (
          <p className="rn-limit-line">{includedLimitLine(night.includedLimit)}</p>
        )}
        {needsChoice(memberOptions) && (
          <PaymentChoice
            legend="Your seat"
            idPrefix="rn-pay-member"
            options={memberOptions}
            value={memberPay ?? 'included'}
            onChange={(kind) => onPaymentChange(kind as MemberPayment)}
            buyCreditsTo="/checkout?returnTo=race-nights"
          />
        )}
        {guestsAllowed && guests.map((g, i) => {
          const name = `${g.firstName} ${g.lastName ?? ''}`.trim();
          if (!needsChoice(guestChoices(night, g.rigClass))) return null;
          return (
            <PaymentChoice
              // Guests have no id; their position is their identity on this claim.
              key={i}
              legend={`Guest ${i + 1} · ${name || RIG_NAME[g.rigClass]}`}
              idPrefix={`rn-pay-guest-${i}`}
              options={guestChoices(night, g.rigClass)}
              value={guestPays[i] ?? 'card'}
              buyCreditsTo="/checkout?returnTo=race-nights"
              onChange={(kind) => onGuestsChange(guests.map((s, j) => (j === i ? { ...s, payment: kind as GuestPayment } : s)))}
            />
          );
        })}
      </div>
      )}

      {guests.length > 0 && !named && (
        <p className="rn-hint mb-0">Add every guest&apos;s first and last name to continue.</p>
      )}
      {!fits && <p className="rn-hint mb-0">There aren&apos;t enough seats on those rigs for everyone.</p>}
      {errorBox}

      <div className="wizard-summary">
        <span className="wizard-summary-line">
          {line}
          {quote.cardCents > 0 && <span className="rn-hint d-block">Card seats plus tax. The card step shows the exact charge.</span>}
        </span>
        {amount && (
          <span className="wizard-summary-total"><span className="wizard-summary-amount" data-testid="rn-quote">{amount}</span></span>
        )}
        {short ? (
          <Link to="/checkout?returnTo=race-nights" className="btn btn-primary ms-auto">Buy credits</Link>
        ) : (
          <button type="button" className="btn btn-primary ms-auto" onClick={onClaim} disabled={busy || !fits || !named || !payable}>
            {busy ? 'Booking…' : buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default RaceNightPanel;
